// index.tsx
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { Stack } from '@mui/material'

export type DialogActionsAlignment = 'center' | 'left' | 'right'

/**
 * Props for the {@link DialogBox} component
 */
interface DialogBoxProps {
  /** Whether the dialog is open and ready for input */
  open: boolean;
  /** The title of this DialogBox */
  title: string;
  /** The description of this DialogBox */
  description: string;
  /** The actions presented to the user to execute and close */
  actions: JSX.Element;
  /** Components to display in the contents of the DialogBox */
  content?: JSX.Element;
  /** Test Id associated with this DialogBox */
  'data-testid'?: string;
  /** If true, the dialog stretches to fill the available width */
  fullWidth?: boolean;
  /** Alignment of the dialog actions (e.g., left, right, center) ({@link DialogActionsAlignment}) */
  actionAlignment?: DialogActionsAlignment
}

/**
 * Renders a dialog box with a title, description, optional content, and action buttons.
 *
 * @example
 * ```tsx
 * <DialogBox
 *   open={true}
 *   title="Confirm Action"
 *   description="Are you sure you want to proceed?"
 *   actions={<Button onClick={handleClose}>Close</Button>}
 * />
 * ```
 */
export default function DialogBox(
  {
    open,
    title,
    description,
    actions,
    content,
    'data-testid': testId,
    fullWidth,
    actionAlignment
  }: Readonly<DialogBoxProps>
): JSX.Element {
  return (
    <Dialog
      open={open}
      PaperProps={{
        sx: {
          borderRadius: '4px'
        }
      }}
      data-testid={testId}
      fullWidth={fullWidth}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack width="100%" spacing={2}>
          <DialogContentText>{description}</DialogContentText>
          {content}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{ justifyContent: actionAlignment }}
      >
        {actions}
      </DialogActions>
    </Dialog>
  )
}

DialogBox.defaultProps = {
  content: undefined,
  'data-testid': undefined,
  fullWidth: false,
  actionAlignment: 'right'
}
